import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import RestoreIcon from '@material-ui/icons/Restore';
import EditIcon from '@material-ui/icons/Edit';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { envManagerEditPermission } from '../../permissions';

interface EnvConfig {
  key: string;
  displayName: string;
  namespace: string;
  color: string;
}

interface SecretEntry {
  key: string;
  value: string;
  length: number;
}

interface HistoryEntry {
  id: number;
  author: string;
  created_at: string;
  message: string | null;
  key_count: number;
}

interface DiffEntry {
  key: string;
  oldValue: string;
  newValue: string;
  status: 'added' | 'modified' | 'deleted' | 'unchanged';
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  subTabs: {
    minHeight: 36,
    '& .MuiTab-root': {
      minHeight: 36,
      textTransform: 'none',
      fontSize: '0.8rem',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.5, 2),
    },
  },
  monospace: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  maskedValue: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  successText: {
    color: '#4caf50',
    fontSize: '0.75rem',
    marginTop: 4,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    marginTop: 4,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  textarea: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  chipAdded: {
    backgroundColor: '#4caf50',
    color: '#fff',
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
  chipModified: {
    backgroundColor: '#f57c00',
    color: '#fff',
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
  chipDeleted: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
  chipUnchanged: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
    fontSize: '0.65rem',
    height: 20,
    fontWeight: 600,
  },
}));

function parseEnvContent(text: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const key = trimmed.slice(0, eqIdx).trim();
    const value = trimmed.slice(eqIdx + 1).trim();
    if (key) result[key] = value;
  }
  return result;
}

export const EnvManagerContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const labelSelector = annotations['backstage.io/kubernetes-label-selector'];
  const entityRef = `component:default/${entity.metadata.name}`;

  const { allowed: canEdit } = usePermission({ permission: envManagerEditPermission });

  const [activeTab, setActiveTab] = useState(0);
  const [subTab, setSubTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);

  // Variables state
  const [secrets, setSecrets] = useState<Record<string, { secretName: string; data: SecretEntry[] }>>({});
  const [loadingSecrets, setLoadingSecrets] = useState<Record<string, boolean>>({});
  const [revealedValues, setRevealedValues] = useState<Record<string, string>>({});
  const [revealingKeys, setRevealingKeys] = useState<Record<string, boolean>>({});

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [envText, setEnvText] = useState('');
  const [diffEntries, setDiffEntries] = useState<DiffEntry[] | null>(null);
  const [commitMessage, setCommitMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);

  // Confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // History state
  const [history, setHistory] = useState<Record<string, HistoryEntry[]>>({});
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});
  const [historyDetailDialog, setHistoryDetailDialog] = useState<{ open: boolean; data?: Record<string, string>; entry?: HistoryEntry }>({ open: false });
  const [restoreDialog, setRestoreDialog] = useState<{ open: boolean; entry?: HistoryEntry }>({ open: false });
  const [restoring, setRestoring] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          namespace: c.getOptionalString('namespace') ?? 'default',
          color: c.getOptionalString('color') ?? '#1976d2',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  // Fetch secrets when env tab changes
  const fetchSecrets = useCallback(
    async (envKey: string) => {
      const env = envs.find(e => e.key === envKey);
      if (!env || !labelSelector) return;
      setLoadingSecrets(prev => ({ ...prev, [envKey]: true }));
      try {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/env-manager/secrets?namespace=${encodeURIComponent(env.namespace)}&labelSelector=${encodeURIComponent(labelSelector)}`,
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const result = await res.json();
        setSecrets(prev => ({ ...prev, [envKey]: result }));
      } catch {
        setSecrets(prev => ({ ...prev, [envKey]: { secretName: '', data: [] } }));
      } finally {
        setLoadingSecrets(prev => ({ ...prev, [envKey]: false }));
      }
    },
    [envs, labelSelector, backendBaseUrl, fetchApi],
  );

  useEffect(() => {
    if (!envs.length || !labelSelector) return;
    const envKey = envs[activeTab]?.key;
    if (!envKey || secrets[envKey] || loadingSecrets[envKey]) return;
    fetchSecrets(envKey);
  }, [envs, activeTab, labelSelector, secrets, loadingSecrets, fetchSecrets]);

  // Fetch history
  const fetchHistory = useCallback(
    async (envKey: string) => {
      setLoadingHistory(prev => ({ ...prev, [envKey]: true }));
      try {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/env-manager/history?entityRef=${encodeURIComponent(entityRef)}&environment=${encodeURIComponent(envKey)}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        setHistory(prev => ({ ...prev, [envKey]: result }));
      } catch {
        setHistory(prev => ({ ...prev, [envKey]: [] }));
      } finally {
        setLoadingHistory(prev => ({ ...prev, [envKey]: false }));
      }
    },
    [backendBaseUrl, fetchApi, entityRef],
  );

  useEffect(() => {
    if (!envs.length || subTab !== 1) return;
    const envKey = envs[activeTab]?.key;
    if (!envKey || history[envKey] || loadingHistory[envKey]) return;
    fetchHistory(envKey);
  }, [envs, activeTab, subTab, history, loadingHistory, fetchHistory]);

  // Reveal a single key
  const handleReveal = useCallback(
    async (envKey: string, secretKey: string) => {
      const env = envs.find(e => e.key === envKey);
      const secretData = secrets[envKey];
      if (!env || !secretData) return;
      const revealKey = `${envKey}:${secretKey}`;

      if (revealedValues[revealKey] !== undefined) {
        setRevealedValues(prev => {
          const next = { ...prev };
          delete next[revealKey];
          return next;
        });
        return;
      }

      setRevealingKeys(prev => ({ ...prev, [revealKey]: true }));
      try {
        const res = await fetchApi.fetch(
          `${backendBaseUrl}/api/env-manager/secrets/reveal?namespace=${encodeURIComponent(env.namespace)}&secretName=${encodeURIComponent(secretData.secretName)}&key=${encodeURIComponent(secretKey)}`,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        setRevealedValues(prev => ({ ...prev, [revealKey]: result.value }));
      } catch {
        // ignore
      } finally {
        setRevealingKeys(prev => ({ ...prev, [revealKey]: false }));
      }
    },
    [envs, secrets, revealedValues, backendBaseUrl, fetchApi],
  );

  // Export .env
  const handleExport = useCallback(
    async (envKey: string) => {
      const env = envs.find(e => e.key === envKey);
      const secretData = secrets[envKey];
      if (!env || !secretData) return;
      setExporting(true);
      try {
        const lines: string[] = [];
        for (const entry of secretData.data) {
          const res = await fetchApi.fetch(
            `${backendBaseUrl}/api/env-manager/secrets/reveal?namespace=${encodeURIComponent(env.namespace)}&secretName=${encodeURIComponent(secretData.secretName)}&key=${encodeURIComponent(entry.key)}`,
          );
          if (res.ok) {
            const result = await res.json();
            lines.push(`${entry.key}=${result.value}`);
          }
        }
        await navigator.clipboard.writeText(lines.join('\n'));
      } catch {
        // ignore
      } finally {
        setExporting(false);
      }
    },
    [envs, secrets, backendBaseUrl, fetchApi],
  );

  // Open edit dialog
  const openEditDialog = () => {
    setEnvText('');
    setDiffEntries(null);
    setCommitMessage('');
    setApplyError(null);
    setApplySuccess(null);
    setEditDialogOpen(true);
  };

  // Compute diff
  const computeDiff = useCallback(() => {
    const currentEnv = envs[activeTab];
    if (!currentEnv) return;
    const secretData = secrets[currentEnv.key];
    if (!secretData) return;

    const newData = parseEnvContent(envText);
    const currentKeys = new Set(secretData.data.map(e => e.key));
    const newKeys = new Set(Object.keys(newData));
    const allKeys = new Set([...currentKeys, ...newKeys]);

    const entries: DiffEntry[] = [];
    for (const key of Array.from(allKeys).sort()) {
      const inCurrent = currentKeys.has(key);
      const inNew = newKeys.has(key);
      if (inNew && !inCurrent) {
        entries.push({ key, oldValue: '', newValue: newData[key], status: 'added' });
      } else if (inCurrent && !inNew) {
        entries.push({ key, oldValue: '(existant)', newValue: '', status: 'deleted' });
      } else {
        // We can't compare actual old values since they're masked. Mark as "modified" for new keys
        entries.push({ key, oldValue: '(existant)', newValue: newData[key], status: 'modified' });
      }
    }
    setDiffEntries(entries);
  }, [envs, activeTab, secrets, envText]);

  // Apply changes
  const handleApply = useCallback(async () => {
    const currentEnv = envs[activeTab];
    if (!currentEnv || !labelSelector) return;
    setConfirmDialogOpen(false);
    setApplying(true);
    setApplyError(null);
    setApplySuccess(null);

    const newData = parseEnvContent(envText);
    try {
      const res = await fetchApi.fetch(`${backendBaseUrl}/api/env-manager/secrets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          namespace: currentEnv.namespace,
          labelSelector,
          entityRef,
          environment: currentEnv.key,
          data: newData,
          message: commitMessage || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setApplySuccess('Variables mises à jour avec succès');
      // Refresh secrets
      setSecrets(prev => {
        const next = { ...prev };
        delete next[currentEnv.key];
        return next;
      });
      setRevealedValues({});
      // Refresh history
      setHistory(prev => {
        const next = { ...prev };
        delete next[currentEnv.key];
        return next;
      });
      setTimeout(() => {
        setEditDialogOpen(false);
        fetchSecrets(currentEnv.key);
      }, 1500);
    } catch (err: any) {
      setApplyError(err.message);
    } finally {
      setApplying(false);
    }
  }, [envs, activeTab, labelSelector, entityRef, envText, commitMessage, backendBaseUrl, fetchApi, fetchSecrets]);

  // View history detail
  const handleViewHistory = useCallback(
    async (entry: HistoryEntry) => {
      try {
        const res = await fetchApi.fetch(`${backendBaseUrl}/api/env-manager/history/${entry.id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        setHistoryDetailDialog({ open: true, data: result.data, entry });
      } catch {
        // ignore
      }
    },
    [backendBaseUrl, fetchApi],
  );

  // Restore
  const handleRestore = useCallback(
    async (entry: HistoryEntry) => {
      const currentEnv = envs[activeTab];
      if (!currentEnv || !labelSelector) return;
      setRestoreDialog({ open: false });
      setRestoring(true);
      try {
        const res = await fetchApi.fetch(`${backendBaseUrl}/api/env-manager/restore`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            historyId: entry.id,
            namespace: currentEnv.namespace,
            labelSelector,
            entityRef,
            environment: currentEnv.key,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        // Refresh
        setSecrets(prev => {
          const next = { ...prev };
          delete next[currentEnv.key];
          return next;
        });
        setHistory(prev => {
          const next = { ...prev };
          delete next[currentEnv.key];
          return next;
        });
        setRevealedValues({});
        fetchSecrets(currentEnv.key);
        fetchHistory(currentEnv.key);
      } catch {
        // ignore
      } finally {
        setRestoring(false);
      }
    },
    [envs, activeTab, labelSelector, entityRef, backendBaseUrl, fetchApi, fetchSecrets, fetchHistory],
  );

  if (!labelSelector) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>backstage.io/kubernetes-label-selector</code> pour activer le gestionnaire d'environnement.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentSecrets = secrets[currentEnv.key];
  const isLoadingSecrets = loadingSecrets[currentEnv.key] ?? false;
  const currentHistory = history[currentEnv.key] ?? [];
  const isLoadingHistory = loadingHistory[currentEnv.key] ?? false;

  const statusChipClass = (status: string) => {
    switch (status) {
      case 'added': return classes.chipAdded;
      case 'modified': return classes.chipModified;
      case 'deleted': return classes.chipDeleted;
      default: return classes.chipUnchanged;
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'added': return 'Ajouté';
      case 'modified': return 'Modifié';
      case 'deleted': return 'Supprimé';
      default: return 'Inchangé';
    }
  };

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setSubTab(0);
            setRevealedValues({});
          }}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab key={env.key} label={env.displayName} style={{ color: env.color }} />
          ))}
        </Tabs>

        <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v)}
          className={classes.subTabs}
          indicatorColor="secondary"
          textColor="secondary"
        >
          <Tab label="Variables" />
          <Tab label="Historique" />
        </Tabs>

        <CardContent style={{ padding: 0 }}>
          {/* ── Variables sub-tab ── */}
          {subTab === 0 && (
            <>
              {canEdit && (
                <Box className={classes.toolbar}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={openEditDialog}
                    style={{ textTransform: 'none' }}
                  >
                    Modifier via .env
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={exporting || !currentSecrets?.data?.length}
                    startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileCopyIcon fontSize="small" />}
                    onClick={() => handleExport(currentEnv.key)}
                    style={{ textTransform: 'none' }}
                  >
                    Exporter .env
                  </Button>
                </Box>
              )}

              {isLoadingSecrets ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : !currentSecrets?.data?.length ? (
                <Box className={classes.empty}>
                  <Typography color="textSecondary">
                    Aucun secret détecté dans <strong>{currentEnv.displayName}</strong>
                  </Typography>
                </Box>
              ) : (
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Clé</TableCell>
                      <TableCell>Valeur</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentSecrets.data.map(entry => {
                      const revealKey = `${currentEnv.key}:${entry.key}`;
                      const isRevealed = revealedValues[revealKey] !== undefined;
                      const isRevealing = revealingKeys[revealKey] ?? false;
                      return (
                        <TableRow key={entry.key} hover>
                          <TableCell>
                            <Typography className={classes.monospace}>{entry.key}</Typography>
                          </TableCell>
                          <TableCell>
                            {isRevealed ? (
                              <Typography className={classes.monospace} style={{ wordBreak: 'break-all' }}>
                                {revealedValues[revealKey]}
                              </Typography>
                            ) : (
                              <Typography className={classes.maskedValue}>
                                {'••••••'} ({entry.length} car.)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {canEdit && (
                              <IconButton
                                size="small"
                                onClick={() => handleReveal(currentEnv.key, entry.key)}
                                disabled={isRevealing}
                              >
                                {isRevealing ? (
                                  <CircularProgress size={16} />
                                ) : isRevealed ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}

          {/* ── Historique sub-tab ── */}
          {subTab === 1 && (
            <>
              {isLoadingHistory ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : !currentHistory.length ? (
                <Box className={classes.empty}>
                  <Typography color="textSecondary">Aucun historique disponible</Typography>
                </Box>
              ) : (
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Auteur</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentHistory.map(entry => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(entry.created_at).toLocaleString('fr-FR')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {entry.author.replace('user:default/', '')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.message ?? '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {entry.key_count} variable{entry.key_count > 1 ? 's' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleViewHistory(entry)}
                            style={{ textTransform: 'none', marginRight: 4 }}
                          >
                            Voir
                          </Button>
                          {canEdit && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={restoring}
                              startIcon={restoring ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon fontSize="small" />}
                              onClick={() => setRestoreDialog({ open: true, entry })}
                              style={{ textTransform: 'none' }}
                            >
                              Restaurer
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Edit Dialog ── */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modifier les variables — {currentEnv.displayName}</DialogTitle>
        <DialogContent>
          {!diffEntries ? (
            <>
              <TextField
                multiline
                rows={20}
                fullWidth
                variant="outlined"
                placeholder="Collez votre fichier .env ici..."
                value={envText}
                onChange={e => setEnvText(e.target.value)}
                InputProps={{ classes: { input: classes.textarea } }}
              />
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={computeDiff}
                  disabled={!envText.trim()}
                  style={{ textTransform: 'none' }}
                >
                  Analyser les changements
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Clé</TableCell>
                    <TableCell>Ancienne valeur</TableCell>
                    <TableCell>Nouvelle valeur</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diffEntries.map(entry => (
                    <TableRow key={entry.key}>
                      <TableCell>
                        <Typography className={classes.monospace}>{entry.key}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.oldValue || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                          {entry.newValue || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel(entry.status)}
                          className={statusChipClass(entry.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box mt={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Message (optionnel)"
                  placeholder="Description des changements..."
                  value={commitMessage}
                  onChange={e => setCommitMessage(e.target.value)}
                  size="small"
                />
              </Box>
              {applyError && (
                <Typography className={classes.errorText} style={{ marginTop: 8 }}>{applyError}</Typography>
              )}
              {applySuccess && (
                <Typography className={classes.successText} style={{ marginTop: 8 }}>{applySuccess}</Typography>
              )}
              <Box mt={2} display="flex" style={{ gap: 8 }}>
                <Button
                  variant="text"
                  onClick={() => setDiffEntries(null)}
                  style={{ textTransform: 'none' }}
                >
                  Retour
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={applying || !!applySuccess}
                  startIcon={applying ? <CircularProgress size={14} color="inherit" /> : undefined}
                  onClick={() => setConfirmDialogOpen(true)}
                  style={{ textTransform: 'none' }}
                >
                  Appliquer
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Apply Dialog ── */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmer les modifications</DialogTitle>
        <DialogContent>
          <Typography>
            Les variables d'environnement de <strong>{currentEnv.displayName}</strong> vont être mises à jour
            et un rolling restart sera effectué.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Annuler</Button>
          <Button color="primary" onClick={handleApply}>Confirmer</Button>
        </DialogActions>
      </Dialog>

      {/* ── History Detail Dialog ── */}
      <Dialog open={historyDetailDialog.open} onClose={() => setHistoryDetailDialog({ open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          Détail — {historyDetailDialog.entry?.message ?? `Version #${historyDetailDialog.entry?.id}`}
        </DialogTitle>
        <DialogContent>
          {historyDetailDialog.data && (
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clé</TableCell>
                  <TableCell>Valeur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(historyDetailDialog.data).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography className={classes.monospace}>{key}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                        {value}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDetailDialog({ open: false })}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Restore Confirm Dialog ── */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false })}>
        <DialogTitle>Confirmer la restauration</DialogTitle>
        <DialogContent>
          <Typography>
            Les variables d'environnement de <strong>{currentEnv.displayName}</strong> seront restaurées
            à la version du {restoreDialog.entry ? new Date(restoreDialog.entry.created_at).toLocaleString('fr-FR') : ''}.
            Un rolling restart sera effectué.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false })}>Annuler</Button>
          <Button
            color="primary"
            onClick={() => restoreDialog.entry && handleRestore(restoreDialog.entry)}
          >
            Restaurer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
